import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ListChecks, RefreshCw, Package, AlertCircle, Loader, Check, X, CheckCircle, Trash2, Edit3 } from 'lucide-react';
import { pickingService } from '../../../../services/pickingService';
import { filterGroupService } from '../../../../services/filterGroupService';
import { FilterGroupDto } from '../../../../types/filterGroup';
import { PListDto, PListDetailsDto } from '../../../../types/picking';
import { BackButton } from '../../../common/BackButton';
import { useWarehouse } from '../../../../contexts/WarehouseContext';

// Import the reusable warehouse table system
import { WarehouseTable, createColumnFactory } from '../../../shared/table';
import type { ColumnDefinition, ExpandableRowConfig, TableDataRow, RowAction } from '../../../shared/table';

// Extended type for table
interface PListTableRow extends TableDataRow {
  id: number;
  pListID: number;
  pListStatus: number;
  statusText: string;
  description1: string | null;
  description2: string | null;
  carrierName: string | null;
  packageRemark: string | null;
  pListDate: string | null;
  deliveryDate: string | null;
  numOfOrderLines: number;
  weight: number | null;
  volum: number | null;
  operatorID: string | null;
  priority: number;
  pListIsBatchYesNo: boolean | null;
  filterGroupID: string | null;
  lockedBy: string | null;
  // Keep original for updates
  _original: PListDto;
}

// Create column factory for type-safe column definitions
const columnFactory = createColumnFactory<PListTableRow>();

// Editable field types for inline editing
type EditableTextField = 'operatorID' | 'carrierName' | 'packageRemark' | 'filterGroupID';
type EditableNumberField = 'priority' | 'weight' | 'volum';
type EditableField = EditableTextField | EditableNumberField;

interface EditableCellState {
  [pListId: number]: {
    priority?: number;
    operatorID?: string;
    carrierName?: string;
    packageRemark?: string;
    weight?: number;
    volum?: number;
    filterGroupID?: string;
  };
}

interface NewPickingListsProps {
  onBack?: () => void;
}

const NewPickingLists: React.FC<NewPickingListsProps> = ({ onBack }) => {
  const { currentWarehouse } = useWarehouse();
  const [pickingLists, setPickingLists] = useState<PListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Expanded state and details
  const [listDetails, setListDetails] = useState<Map<number, PListDetailsDto>>(new Map());
  const [loadingDetails, setLoadingDetails] = useState<Set<number>>(new Set());

  // Inline editing state
  const [editState, setEditState] = useState<EditableCellState>({});
  const [savingPLists, setSavingPLists] = useState<Set<number>>(new Set());
  const [savedPLists, setSavedPLists] = useState<Set<number>>(new Set());

  // Selection state
  const [selectedPListIds, setSelectedPListIds] = useState<number[]>([]);

  // FilterGroup state
  const [filterGroups, setFilterGroups] = useState<FilterGroupDto[]>([]);

  // Approval state
  const [approvingPLists, setApprovingPLists] = useState(false);
  const [showApprovalResult, setShowApprovalResult] = useState(false);
  const [approvalResult, setApprovalResult] = useState<any>(null);

  // Delete state
  const [deletingPListId, setDeletingPListId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pListToDelete, setPListToDelete] = useState<PListDto | null>(null);

  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Refs to access current state in stable callbacks (prevents columns array from being recreated)
  const editStateRef = useRef<EditableCellState>(editState);
  const pickingListsRef = useRef<PListDto[]>(pickingLists);

  // Keep refs in sync with state
  useEffect(() => {
    editStateRef.current = editState;
  }, [editState]);

  useEffect(() => {
    pickingListsRef.current = pickingLists;
  }, [pickingLists]);

  useEffect(() => {
    loadPickingLists();
    loadFilterGroups();
  }, []);

  const loadFilterGroups = async () => {
    try {
      const groups = await filterGroupService.getCurrent();
      setFilterGroups(groups);
    } catch (err) {
      console.error('Error loading filter groups:', err);
    }
  };

  const loadPickingLists = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pickingService.getPickingListsByStatus(0, currentWarehouse || undefined); // Status 0 = Nye
      setPickingLists(data);

      // Initialize edit state
      const newEditState: EditableCellState = {};
      data.forEach(pl => {
        newEditState[pl.pListID] = {
          priority: pl.priority,
          operatorID: pl.operatorID || ''
        };
      });
      setEditState(newEditState);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feil ved lasting av plukklister');
      console.error('Error loading picking lists:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPickingListDetails = async (pListId: number) => {
    try {
      setLoadingDetails(prev => new Set(prev).add(pListId));
      const details = await pickingService.getPickingDetails(pListId);
      setListDetails(prev => new Map(prev).set(pListId, details));
    } catch (err) {
      console.error(`Error loading details for picking list ${pListId}:`, err);
    } finally {
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(pListId);
        return newSet;
      });
    }
  };

  // Transform to table data
  const tableData: PListTableRow[] = useMemo(() => {
    return pickingLists.map(pl => ({
      id: pl.pListID,
      pListID: pl.pListID,
      pListStatus: pl.pListStatus,
      statusText: pl.statusText,
      description1: pl.description1 || null,
      description2: pl.description2 || null,
      carrierName: pl.carrierName || null,
      packageRemark: pl.packageRemark || null,
      pListDate: pl.pListDate || null,
      deliveryDate: pl.deliveryDate || null,
      numOfOrderLines: pl.numOfOrderLines || 0,
      weight: pl.weight ?? null,
      volum: pl.volum ?? null,
      operatorID: pl.operatorID || null,
      priority: pl.priority,
      pListIsBatchYesNo: pl.pListIsBatchYesNo ?? null,
      filterGroupID: pl.filterGroupID || null,
      lockedBy: pl.lockedBy || null,
      _original: pl
    }));
  }, [pickingLists]);

  // Row ID function
  const getRowId = useCallback((row: PListTableRow) => row.pListID, []);

  // Handle selection change
  const handleSelectionChange = useCallback((selectedRows: (string | number)[], selectedData: PListTableRow[]) => {
    setSelectedPListIds(selectedRows as number[]);
  }, []);

  // Save inline edit for a specific field - uses refs for stable callback
  const saveInlineEdit = useCallback(async (
    pListId: number,
    field?: EditableField,
    newFieldValue?: string | number
  ) => {
    const editData = editStateRef.current[pListId];
    const originalPickList = pickingListsRef.current.find(p => p.pListID === pListId);
    if (!originalPickList) return;

    // If a specific field is provided, save just that field
    if (field) {
      let newValue: string | number | undefined;
      if (newFieldValue !== undefined) {
        newValue = newFieldValue;
      } else if (editData) {
        newValue = editData[field] as string | number | undefined;
      } else {
        return;
      }

      // Check if value actually changed
      const oldValue = (originalPickList as any)[field] ?? '';
      if (String(newValue) === String(oldValue)) return;

      try {
        setSavingPLists(prev => new Set(prev).add(pListId));

        const pickListUpdate = {
          ...originalPickList,
          [field]: newValue === '' ? null : newValue
        };

        await pickingService.updatePickingList(pListId, pickListUpdate);

        // Update local state
        setPickingLists(prev => prev.map(p =>
          p.pListID === pListId ? { ...p, [field]: newValue === '' ? null : newValue } : p
        ));

        // Show saved indicator
        setSavedPLists(prev => new Set(prev).add(pListId));
        setTimeout(() => {
          setSavedPLists(prev => {
            const newSet = new Set(prev);
            newSet.delete(pListId);
            return newSet;
          });
        }, 2000);
      } catch (err) {
        console.error('Feil ved lagring:', err);
        alert(`Feil ved lagring: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
      } finally {
        setSavingPLists(prev => {
          const newSet = new Set(prev);
          newSet.delete(pListId);
          return newSet;
        });
      }
      return;
    }

    // Legacy: save all changed fields at once (for priority/operatorID backward compat)
    if (!editData) return;

    if (editData.priority === originalPickList.priority &&
        editData.operatorID === (originalPickList.operatorID || '')) {
      return;
    }

    try {
      setSavingPLists(prev => new Set(prev).add(pListId));

      const pickListUpdate = {
        ...originalPickList,
        priority: editData.priority ?? originalPickList.priority,
        operatorID: editData.operatorID !== undefined ? editData.operatorID : originalPickList.operatorID
      };

      await pickingService.updatePickingList(pListId, pickListUpdate);

      // Update local state
      setPickingLists(prev => prev.map(p =>
        p.pListID === pListId ? { ...p, ...pickListUpdate } : p
      ));

      // Show saved indicator
      setSavedPLists(prev => new Set(prev).add(pListId));
      setTimeout(() => {
        setSavedPLists(prev => {
          const newSet = new Set(prev);
          newSet.delete(pListId);
          return newSet;
        });
      }, 2000);

    } catch (err) {
      console.error('Feil ved lagring:', err);
      alert(`Feil ved lagring: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setSavingPLists(prev => {
        const newSet = new Set(prev);
        newSet.delete(pListId);
        return newSet;
      });
    }
  }, []); // No dependencies - uses refs

  // Inline editable priority cell - stable callback using refs
  const renderPriorityCell = useCallback((value: number, row: PListTableRow) => {
    const pListId = row.pListID;
    const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
    const isSaving = savingPLists.has(pListId);
    const isSaved = savedPLists.has(pListId);

    return (
      <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
        <input
          key={`priority-${pListId}-${value}`}
          type="number"
          defaultValue={value}
          onChange={(e) => {
            e.stopPropagation();
            const newValue = parseInt(e.target.value) || 0;
            setEditState(prev => ({
              ...prev,
              [pListId]: { ...prev[pListId], priority: newValue }
            }));
          }}
          onBlur={() => saveInlineEdit(pListId, 'priority')}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              saveInlineEdit(pListId, 'priority');
              (e.target as HTMLInputElement).blur();
            }
          }}
          onClick={stopAll}
          onMouseDown={stopAll}
          onFocus={stopAll}
          disabled={isSaving}
          className={`w-16 px-2 py-1 text-xs text-right border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
            isSaving ? 'opacity-50' : ''
          } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
        />
        {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
      </div>
    );
  }, [savingPLists, savedPLists, saveInlineEdit]);

  // Inline editable operator cell - stable callback using refs
  const renderOperatorCell = useCallback((value: string | null, row: PListTableRow) => {
    const pListId = row.pListID;
    const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
    const isSaving = savingPLists.has(pListId);
    const isSaved = savedPLists.has(pListId);

    return (
      <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
        <input
          key={`operator-${pListId}-${value}`}
          type="text"
          defaultValue={value || ''}
          onChange={(e) => {
            e.stopPropagation();
            setEditState(prev => ({
              ...prev,
              [pListId]: { ...prev[pListId], operatorID: e.target.value }
            }));
          }}
          onBlur={() => saveInlineEdit(pListId, 'operatorID')}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              saveInlineEdit(pListId, 'operatorID');
              (e.target as HTMLInputElement).blur();
            }
          }}
          onClick={stopAll}
          onMouseDown={stopAll}
          onFocus={stopAll}
          disabled={isSaving}
          className={`w-full px-2 py-1 text-xs border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
            isSaving ? 'opacity-50' : ''
          } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          placeholder="-"
        />
        {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
      </div>
    );
  }, [savingPLists, savedPLists, saveInlineEdit]);

  // Generic renderer for editable text cells
  const renderEditableText = useCallback((field: EditableTextField) => {
    return (value: string | null, row: PListTableRow) => {
      const pListId = row.pListID;
      const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
      const isSaving = savingPLists.has(pListId);
      const isSaved = savedPLists.has(pListId);

      return (
        <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
          <input
            key={`${field}-${pListId}-${value}`}
            type="text"
            defaultValue={value || ''}
            onChange={(e) => {
              e.stopPropagation();
              setEditState(prev => ({
                ...prev,
                [pListId]: { ...prev[pListId], [field]: e.target.value }
              }));
            }}
            onBlur={() => saveInlineEdit(pListId, field)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                saveInlineEdit(pListId, field);
                (e.target as HTMLInputElement).blur();
              }
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onFocus={stopAll}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-xs border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
              isSaving ? 'opacity-50' : ''
            } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          />
          {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
        </div>
      );
    };
  }, [savingPLists, savedPLists, saveInlineEdit]);

  // Generic renderer for editable number cells
  const renderEditableNumber = useCallback((field: EditableNumberField) => {
    return (value: number | null, row: PListTableRow) => {
      const pListId = row.pListID;
      const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
      const isSaving = savingPLists.has(pListId);
      const isSaved = savedPLists.has(pListId);

      return (
        <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
          <input
            key={`${field}-${pListId}-${value}`}
            type="number"
            step="any"
            defaultValue={value ?? ''}
            onChange={(e) => {
              e.stopPropagation();
              const numVal = e.target.value === '' ? '' : parseFloat(e.target.value);
              setEditState(prev => ({
                ...prev,
                [pListId]: { ...prev[pListId], [field]: numVal }
              }));
            }}
            onBlur={() => saveInlineEdit(pListId, field)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                saveInlineEdit(pListId, field);
                (e.target as HTMLInputElement).blur();
              }
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onFocus={stopAll}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-xs text-right border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
              isSaving ? 'opacity-50' : ''
            } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          />
          {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
        </div>
      );
    };
  }, [savingPLists, savedPLists, saveInlineEdit]);

  // FilterGroup dropdown renderer
  const renderFilterGroupDropdown = useCallback((value: string | null, row: PListTableRow) => {
    const pListId = row.pListID;
    const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
    const isSaving = savingPLists.has(pListId);
    const isSaved = savedPLists.has(pListId);

    return (
      <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
        <select
          key={`filterGroup-${pListId}-${value}-${filterGroups.length}`}
          defaultValue={value || ''}
          onChange={(e) => {
            e.stopPropagation();
            saveInlineEdit(pListId, 'filterGroupID', e.target.value);
          }}
          onClick={stopAll}
          onMouseDown={stopAll}
          onFocus={stopAll}
          disabled={isSaving}
          className={`w-full px-2 py-1 text-xs border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
            isSaving ? 'opacity-50' : ''
          } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
        >
          <option value="">— Ingen —</option>
          {filterGroups.map(fg => (
            <option key={fg.filterGroupID} value={fg.filterGroupID}>
              {fg.filterGroupID} - {fg.filterGroupDescr}
            </option>
          ))}
        </select>
        {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
      </div>
    );
  }, [savingPLists, savedPLists, saveInlineEdit, filterGroups]);

  // Row actions (Handling column)
  const rowActions: RowAction<PListTableRow>[] = useMemo(() => [
    {
      id: 'delete-plist',
      label: 'Slett plukkliste',
      icon: Trash2,
      variant: 'danger' as const,
      disabled: (row: PListTableRow) => !!row.lockedBy,
      onClick: (row: PListTableRow) => {
        setPListToDelete(row._original);
        setShowDeleteConfirm(true);
      }
    }
  ], []);

  // Define columns based on legacy PlistNew.dfm
  const columns: ColumnDefinition<PListTableRow>[] = useMemo(() => [
    columnFactory.createNumberColumn('pListID', 'pListID', 'Plukklistenr.', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value !== null && value !== undefined ? String(value) : '-'
    }),
    columnFactory.createTextColumn('statusText', 'statusText', 'Status', {
      width: 110,
      sortable: true,
      filterType: 'dropdown',
      renderer: (value, row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(row.pListStatus)}`}>
          {value}
        </span>
      )
    }),
    columnFactory.createTextColumn('description1', 'description1', 'Beskrivelse', {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('description2', 'description2', 'Beskrivelse 2', {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('carrierName', 'carrierName', 'Transportør', {
      width: 150,
      sortable: true,
      filterType: 'both',
      renderer: renderEditableText('carrierName')
    }),
    columnFactory.createTextColumn('packageRemark', 'packageRemark', 'Pakkekommentar', {
      width: 180,
      sortable: true,
      filterType: 'both',
      renderer: renderEditableText('packageRemark')
    }),
    columnFactory.createDateColumn('pListDate', 'pListDate', 'Opprettet dato', {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createDateColumn('deliveryDate', 'deliveryDate', 'Leveringsdato', {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('numOfOrderLines', 'numOfOrderLines', 'Ant. linjer', {
      width: 90,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('weight', 'weight', 'Vekt', {
      width: 100,
      sortable: true,
      filterType: 'both',
      renderer: renderEditableNumber('weight')
    }),
    columnFactory.createNumberColumn('volum', 'volum', 'Volum', {
      width: 100,
      sortable: true,
      filterType: 'both',
      renderer: renderEditableNumber('volum')
    }),
    columnFactory.createNumberColumn('priority', 'priority', 'Prioritet', {
      width: 100,
      sortable: true,
      filterType: 'both',
      renderer: renderPriorityCell
    }),
    columnFactory.createTextColumn('operatorID', 'operatorID', 'Operatør', {
      width: 130,
      sortable: true,
      filterType: 'both',
      renderer: renderOperatorCell
    }),
    columnFactory.createTextColumn('pListIsBatchYesNo', 'pListIsBatchYesNo', 'Samplukk', {
      width: 90,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? 'Ja' : value === false ? 'Nei' : '-',
      visible: false
    }),
    columnFactory.createTextColumn('filterGroupID', 'filterGroupID', 'Filtergruppe', {
      width: 200,
      sortable: true,
      filterType: 'both',
      renderer: renderFilterGroupDropdown
    }),
    columnFactory.createTextColumn('lockedBy', 'lockedBy', 'Last av', {
      width: 100,
      sortable: true,
      filterType: 'both',
      renderer: (value) => value ? (
        <span className="text-yellow-600 text-xs flex items-center">
          <AlertCircle size={12} className="mr-1" />
          {value}
        </span>
      ) : <span className="text-gray-400 text-xs">-</span>
    }),
  ], [renderPriorityCell, renderOperatorCell, renderEditableText, renderEditableNumber, renderFilterGroupDropdown]);

  const getStatusColor = (status: number): string => {
    switch (status) {
      case 0: return 'bg-gray-100 text-gray-800';
      case 10: return 'bg-green-100 text-green-800';
      case 20: return 'bg-blue-100 text-blue-800';
      case 30: return 'bg-yellow-100 text-yellow-800';
      case 40: return 'bg-green-100 text-green-800';
      case 45: return 'bg-purple-100 text-purple-800';
      case 50: return 'bg-indigo-100 text-indigo-800';
      case 60: return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Approval handler
  const handleApprovePLists = async () => {
    if (selectedPListIds.length === 0) {
      alert('Vennligst velg minst én plukkliste å godkjenne');
      return;
    }

    if (!window.confirm(`Er du sikker på at du vil godkjenne ${selectedPListIds.length} plukkliste${selectedPListIds.length > 1 ? 'r' : ''}?`)) {
      return;
    }

    try {
      setApprovingPLists(true);

      // Load details for selected PLists to get order numbers
      const orderNumbersSet = new Set<number>();

      for (const pListId of selectedPListIds) {
        try {
          const details = await pickingService.getPickingDetails(pListId);
          details.lines.forEach(line => {
            if (line.orderNo) {
              orderNumbersSet.add(line.orderNo);
            }
          });
        } catch (err) {
          console.error(`Error loading details for PList ${pListId}:`, err);
        }
      }

      const orderNumbers = Array.from(orderNumbersSet);

      if (orderNumbers.length === 0) {
        alert('Ingen ordre funnet for valgte plukklister');
        return;
      }

      const result = await pickingService.batchApprovePListsByOrders(orderNumbers);

      setApprovalResult(result);
      setShowApprovalResult(true);
      setSelectedPListIds([]);

      await loadPickingLists();

    } catch (err) {
      console.error('Error approving PLists:', err);
      alert(`Feil ved godkjenning: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setApprovingPLists(false);
    }
  };

  // Delete handlers
  const handleConfirmDelete = async () => {
    if (!pListToDelete) return;

    try {
      setDeletingPListId(pListToDelete.pListID);
      setShowDeleteConfirm(false);

      const result = await pickingService.deletePickingList(pListToDelete.pListID);

      if (result.success) {
        setPickingLists(prev => prev.filter(p => p.pListID !== pListToDelete.pListID));
        alert(result.message);
      }
    } catch (err) {
      console.error('Error deleting PList:', err);
      alert(`Feil ved sletting: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setDeletingPListId(null);
      setPListToDelete(null);
    }
  };

  // Detail renderer for expandable rows
  const renderPListLines = useCallback((pList: PListTableRow, details: PListDetailsDto | undefined) => {
    const isLoadingDetails = loadingDetails.has(pList.pListID);

    if (isLoadingDetails) {
      return (
        <div className="flex items-center justify-center py-8 bg-slate-50">
          <Loader className="animate-spin mr-2" size={20} />
          <span className="text-gray-600">Laster linjer...</span>
        </div>
      );
    }

    if (!details) {
      return (
        <div className="text-center py-8 text-gray-500 bg-slate-50">
          Kunne ikke laste linjer
        </div>
      );
    }

    return (
      <div className="bg-slate-50 p-3 border-t border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
          <Package size={16} />
          <span>Plukkliste-linjer for {pList.pListID}</span>
          <span className="text-xs text-gray-500">
            ({details.completedLines} av {details.totalLines} fullført - {details.completionPercentage.toFixed(0)}%)
          </span>
        </h4>

        {details.lines.length > 0 ? (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100">
                <th className="text-left py-2 px-3 font-medium text-slate-500">Linjenr</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Ordrenr</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Ordre linje</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Varenr</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">Antall</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">Reservert</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">Plukket</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">Gjenstående</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Lokasjon</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Lager</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Status</th>
                <th className="text-center py-2 px-3 font-medium text-slate-500">Fullført</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {details.lines.map((line) => (
                <tr key={line.id} className="hover:bg-slate-100 transition-colors">
                  <td className="py-2 px-3 text-slate-900 font-medium">{line.pListLineNo}</td>
                  <td className="py-2 px-3 text-slate-900">{line.orderNo}</td>
                  <td className="py-2 px-3 text-slate-900">{line.lineNo}</td>
                  <td className="py-2 px-3 text-slate-900 font-mono">{line.itemNo || '-'}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qty.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qtyReserved.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qtyPicked.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900 text-right font-medium">{line.remainingQty.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900">{line.binCode || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.warehouseID || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.lineStatus}</td>
                  <td className="py-2 px-3 text-center">
                    {line.isCompleted ? (
                      <Check className="text-green-600 mx-auto" size={16} />
                    ) : (
                      <X className="text-gray-400 mx-auto" size={16} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Ingen linjer funnet for denne plukklisten
          </div>
        )}
      </div>
    );
  }, [loadingDetails]);

  // Expandable configuration
  const expandableConfig: ExpandableRowConfig<PListTableRow, PListDetailsDto | undefined> = useMemo(() => ({
    getDetailData: (pList) => listDetails.get(pList.pListID),
    renderDetail: renderPListLines,
    expandOnRowClick: false,
    onExpand: async (pList) => {
      if (!listDetails.has(pList.pListID)) {
        await loadPickingListDetails(pList.pListID);
      }
    }
  }), [listDetails, renderPListLines]);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'approve',
      label: `Godkjenn (${selectedPListIds.length})`,
      icon: CheckCircle,
      onClick: handleApprovePLists,
      disabled: () => approvingPLists || selectedPListIds.length === 0,
      variant: 'primary' as const
    },
    {
      id: 'refresh',
      label: 'Oppdater',
      icon: RefreshCw,
      onClick: loadPickingLists,
      variant: 'secondary' as const
    }
  ], [selectedPListIds.length, approvingPLists, handleApprovePLists]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={20} />
          <span>Laster plukklister...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
        <h3 className="text-red-800 font-medium">Feil ved lasting av plukklister</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={loadPickingLists}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>Prøv igjen</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center space-x-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <ListChecks size={24} />
            <span>Nye Plukklister</span>
          </h1>
          <span className="text-sm text-gray-500">
            {tableData.length} plukklister
          </span>
        </div>
        <p className="text-gray-500 text-sm mt-1 ml-12">
          <Edit3 size={12} className="inline mr-1" />
          Filtergruppe, Transportør, Pakkekommentar, Prioritet, Vekt og Volum er redigerbare - klikk for å endre
        </p>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 px-4 py-2">
        <WarehouseTable
          data={tableData}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: false,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            },
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'new-picking-lists-table'
          }}
          selectedRows={selectedPListIds}
          onSelectionChange={handleSelectionChange}
          getRowId={getRowId}
          rowActions={rowActions}
          tableActions={tableActions}
          expandableConfig={expandableConfig}
          loading={false}
          error={undefined}
          aria-label="Nye plukklister tabell"
        />
      </div>

      {/* Approval Result Modal */}
      {showApprovalResult && approvalResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-green-600 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CheckCircle size={24} />
                <h2 className="text-xl font-semibold">Godkjenning Fullført</h2>
              </div>
              <button
                onClick={() => setShowApprovalResult(false)}
                className="text-white hover:bg-green-700 rounded p-1"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-blue-600 text-sm font-medium">Totalt Behandlet</div>
                  <div className="text-2xl font-bold text-blue-900">{approvalResult.totalProcessed || 0}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-green-600 text-sm font-medium">Godkjent</div>
                  <div className="text-2xl font-bold text-green-900">{approvalResult.successCount || 0}</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="text-yellow-600 text-sm font-medium">Hoppet Over</div>
                  <div className="text-2xl font-bold text-yellow-900">{approvalResult.skippedCount || 0}</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="text-red-600 text-sm font-medium">Feilet</div>
                  <div className="text-2xl font-bold text-red-900">{approvalResult.failedCount || 0}</div>
                </div>
              </div>

              {/* Detailed Results */}
              {approvalResult.results && approvalResult.results.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Detaljert Resultat</h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">PList ID</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ordre</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Melding</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {approvalResult.results.map((result: any, index: number) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm font-medium text-gray-900">{result.pListId}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{result.orderNo || '-'}</td>
                            <td className="px-4 py-2 text-sm">
                              {result.success ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <Check size={12} className="mr-1" />
                                  Godkjent
                                </span>
                              ) : result.skipped ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Hoppet Over
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  <X size={12} className="mr-1" />
                                  Feilet
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">{result.message || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Errors */}
              {approvalResult.errors && approvalResult.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-red-800 font-semibold mb-2">Feil</h3>
                  <ul className="list-disc list-inside space-y-1">
                    {approvalResult.errors.map((error: string, index: number) => (
                      <li key={index} className="text-red-700 text-sm">{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowApprovalResult(false)}
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && pListToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg flex items-center space-x-3">
              <Trash2 size={24} />
              <h2 className="text-xl font-semibold">Bekreft sletting</h2>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Er du sikker på at du vil slette plukkliste <strong>{pListToDelete.pListID}</strong>?
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Beskrivelse:</div>
                  <div className="text-gray-900">{pListToDelete.description1 || '-'}</div>
                  <div className="text-gray-500">Antall linjer:</div>
                  <div className="text-gray-900">{pListToDelete.numOfOrderLines || 0}</div>
                  <div className="text-gray-500">Status:</div>
                  <div className="text-gray-900">{pListToDelete.statusText}</div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <strong>Advarsel:</strong> Denne handlingen kan ikke angres. Plukklisten og alle tilhørende linjer vil bli permanent slettet.
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPListToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Avbryt
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2"
              >
                <Trash2 size={16} />
                <span>Slett plukkliste</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewPickingLists;
